import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';

interface ElasticsearchConfig {
  url: string;
  username: string;
  password: string;
  indexName: string;
  numberOfShards: number;
  numberOfReplicas: number;
  refreshInterval: string;
  analyzerLanguage: string;
  highlightFields: string[];
  searchFields: string[];
  filterFields: string[];
}

interface Neo4jConfig {
  url: string;
  username: string;
  password: string;
  database: string;
  maxConnectionPoolSize: number;
  connectionTimeout: number;
  trustStrategy: 'TRUST_ALL_CERTIFICATES' | 'TRUST_SYSTEM_CA_SIGNED_CERTIFICATES';
  encryptionLevel: 'REQUIRED' | 'OPTIONAL' | 'DISABLED';
  relationshipTypes: string[];
  nodeLabels: string[];
}

interface SearchConfig {
  id: string;
  activeSearchEngine: 'elasticsearch' | 'neo4j';
  elasticsearch: ElasticsearchConfig;
  neo4j: Neo4jConfig;
}

const initialConfig: SearchConfig = {
  id: '',
  activeSearchEngine: 'elasticsearch',
  elasticsearch: {
    url: '',
    username: '',
    password: '',
    indexName: 'documents',
    numberOfShards: 1,
    numberOfReplicas: 1,
    refreshInterval: '1s',
    analyzerLanguage: 'portuguese',
    highlightFields: ['content', 'title'],
    searchFields: ['content', 'title', 'tags'],
    filterFields: ['documentType', 'status', 'createdBy']
  },
  neo4j: {
    url: '',
    username: '',
    password: '',
    database: 'neo4j',
    maxConnectionPoolSize: 100,
    connectionTimeout: 30000,
    trustStrategy: 'TRUST_SYSTEM_CA_SIGNED_CERTIFICATES',
    encryptionLevel: 'REQUIRED',
    relationshipTypes: ['RELATED_TO', 'REFERENCES', 'SIMILAR_TO'],
    nodeLabels: ['Document', 'Category', 'Tag']
  }
};

type TabType = 'elasticsearch' | 'neo4j';

export const SearchConfigPage: React.FC = () => {
  const [config, setConfig] = useState<SearchConfig>(initialConfig);
  const [activeTab, setActiveTab] = useState<TabType>('elasticsearch');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const { addToast } = useToast();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/system/search-config');
      setConfig(response.data);
    } catch (error: any) {
      let msg = 'Erro ao carregar configurações de pesquisa.';
      if (error.response && error.response.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      addToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    section: 'elasticsearch' | 'neo4j',
    field: string,
    value: string | number | string[]
  ) => {
    setConfig({
      ...config,
      [section]: {
        ...config[section],
        [field]: value
      }
    });
  };

  const handleArrayInputChange = (
    section: 'elasticsearch' | 'neo4j',
    field: string,
    value: string
  ) => {
    handleInputChange(section, field, value.split(',').map(item => item.trim()));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/system/search-config', config);
      addToast('Configurações de pesquisa salvas com sucesso!', 'success');
    } catch (error: any) {
      let msg = 'Erro ao salvar configurações de pesquisa.';
      if (error.response && error.response.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      addToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (engine: 'elasticsearch' | 'neo4j') => {
    setTestingConnection(true);
    try {
      await api.post(`/system/search-config/test-connection/${engine}`);
      addToast('Conexão estabelecida com sucesso!', 'success');
    } catch (error: any) {
      let msg = 'Erro ao testar conexão.';
      if (error.response && error.response.data) {
        msg = error.response.data.message || error.response.data.title || msg;
      }
      addToast(msg, 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const renderElasticsearchConfig = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Configurações de Conexão</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-1">URL do Elasticsearch</label>
            <input
              type="text"
              value={config.elasticsearch.url}
              onChange={(e) => handleInputChange('elasticsearch', 'url', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="http://localhost:9200"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Nome do Índice</label>
            <input
              type="text"
              value={config.elasticsearch.indexName}
              onChange={(e) => handleInputChange('elasticsearch', 'indexName', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="documents"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Usuário</label>
            <input
              type="text"
              value={config.elasticsearch.username}
              onChange={(e) => handleInputChange('elasticsearch', 'username', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="elastic"
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Senha</label>
            <input
              type="password"
              value={config.elasticsearch.password}
              onChange={(e) => handleInputChange('elasticsearch', 'password', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="••••••••"
              maxLength={20}
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-4">Configurações do Índice</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-1">Número de Shards</label>
            <input
              type="number"
              value={config.elasticsearch.numberOfShards}
              onChange={(e) => handleInputChange('elasticsearch', 'numberOfShards', parseInt(e.target.value))}
              className="w-full border rounded px-3 py-2"
              min="1"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Número de Réplicas</label>
            <input
              type="number"
              value={config.elasticsearch.numberOfReplicas}
              onChange={(e) => handleInputChange('elasticsearch', 'numberOfReplicas', parseInt(e.target.value))}
              className="w-full border rounded px-3 py-2"
              min="0"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Intervalo de Atualização</label>
            <input
              type="text"
              value={config.elasticsearch.refreshInterval}
              onChange={(e) => handleInputChange('elasticsearch', 'refreshInterval', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="1s"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Idioma do Analisador</label>
            <select
              value={config.elasticsearch.analyzerLanguage}
              onChange={(e) => handleInputChange('elasticsearch', 'analyzerLanguage', e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="portuguese">Português</option>
              <option value="english">Inglês</option>
              <option value="spanish">Espanhol</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-4">Campos de Pesquisa</h3>
        <div className="space-y-4">
          <div>
            <label className="block font-medium mb-1">Campos de Destaque</label>
            <input
              type="text"
              value={config.elasticsearch.highlightFields.join(', ')}
              onChange={(e) => handleArrayInputChange('elasticsearch', 'highlightFields', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="content, title"
            />
            <p className="text-sm text-gray-500 mt-1">Separe os campos por vírgula</p>
          </div>
          <div>
            <label className="block font-medium mb-1">Campos de Busca</label>
            <input
              type="text"
              value={config.elasticsearch.searchFields.join(', ')}
              onChange={(e) => handleArrayInputChange('elasticsearch', 'searchFields', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="content, title, tags"
            />
            <p className="text-sm text-gray-500 mt-1">Separe os campos por vírgula</p>
          </div>
          <div>
            <label className="block font-medium mb-1">Campos de Filtro</label>
            <input
              type="text"
              value={config.elasticsearch.filterFields.join(', ')}
              onChange={(e) => handleArrayInputChange('elasticsearch', 'filterFields', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="documentType, status, createdBy"
            />
            <p className="text-sm text-gray-500 mt-1">Separe os campos por vírgula</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => testConnection('elasticsearch')}
          disabled={testingConnection}
        >
          {testingConnection ? 'Testando...' : 'Testar Conexão'}
        </Button>
      </div>
    </div>
  );

  const renderNeo4jConfig = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Configurações de Conexão</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-1">URL do Neo4j</label>
            <input
              type="text"
              value={config.neo4j.url}
              onChange={(e) => handleInputChange('neo4j', 'url', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="bolt://localhost:7687"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Nome do Banco de Dados</label>
            <input
              type="text"
              value={config.neo4j.database}
              onChange={(e) => handleInputChange('neo4j', 'database', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="neo4j"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Usuário</label>
            <input
              type="text"
              value={config.neo4j.username}
              onChange={(e) => handleInputChange('neo4j', 'username', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="neo4j"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Senha</label>
            <input
              type="password"
              value={config.neo4j.password}
              onChange={(e) => handleInputChange('neo4j', 'password', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="••••••••"
              maxLength={20}
              required
              autoComplete="off"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-4">Configurações de Conexão Avançadas</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium mb-1">Tamanho Máximo do Pool</label>
            <input
              type="number"
              value={config.neo4j.maxConnectionPoolSize}
              onChange={(e) => handleInputChange('neo4j', 'maxConnectionPoolSize', parseInt(e.target.value))}
              className="w-full border rounded px-3 py-2"
              min="1"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Timeout de Conexão (ms)</label>
            <input
              type="number"
              value={config.neo4j.connectionTimeout}
              onChange={(e) => handleInputChange('neo4j', 'connectionTimeout', parseInt(e.target.value))}
              className="w-full border rounded px-3 py-2"
              min="1000"
              required
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Estratégia de Confiança</label>
            <select
              value={config.neo4j.trustStrategy}
              onChange={(e) => handleInputChange('neo4j', 'trustStrategy', e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="TRUST_SYSTEM_CA_SIGNED_CERTIFICATES">Certificados CA do Sistema</option>
              <option value="TRUST_ALL_CERTIFICATES">Todos os Certificados</option>
            </select>
          </div>
          <div>
            <label className="block font-medium mb-1">Nível de Criptografia</label>
            <select
              value={config.neo4j.encryptionLevel}
              onChange={(e) => handleInputChange('neo4j', 'encryptionLevel', e.target.value)}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="REQUIRED">Obrigatória</option>
              <option value="OPTIONAL">Opcional</option>
              <option value="DISABLED">Desativada</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-medium mb-4">Configurações do Grafo</h3>
        <div className="space-y-4">
          <div>
            <label className="block font-medium mb-1">Tipos de Relacionamento</label>
            <input
              type="text"
              value={config.neo4j.relationshipTypes.join(', ')}
              onChange={(e) => handleArrayInputChange('neo4j', 'relationshipTypes', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="RELATED_TO, REFERENCES, SIMILAR_TO"
            />
            <p className="text-sm text-gray-500 mt-1">Separe os tipos por vírgula</p>
          </div>
          <div>
            <label className="block font-medium mb-1">Labels dos Nós</label>
            <input
              type="text"
              value={config.neo4j.nodeLabels.join(', ')}
              onChange={(e) => handleArrayInputChange('neo4j', 'nodeLabels', e.target.value)}
              className="w-full border rounded px-3 py-2"
              placeholder="Document, Category, Tag"
            />
            <p className="text-sm text-gray-500 mt-1">Separe os labels por vírgula</p>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => testConnection('neo4j')}
          disabled={testingConnection}
        >
          {testingConnection ? 'Testando...' : 'Testar Conexão'}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return <div className="p-8">Carregando...</div>;
  }

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold mb-6">Configurações de Pesquisa</h1>
      
      <div className="max-w-4xl bg-white rounded shadow">
        <div className="border-b">
          <nav className="flex">
            <button
              className={`px-6 py-3 font-medium ${activeTab === 'elasticsearch' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('elasticsearch')}
            >
              Elasticsearch
            </button>
            <button
              className={`px-6 py-3 font-medium ${activeTab === 'neo4j' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('neo4j')}
            >
              Grafo de Conhecimento
            </button>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <label className="block font-medium mb-2">Motor de Pesquisa Ativo</label>
            <select
              value={config.activeSearchEngine}
              onChange={(e) => setConfig({ ...config, activeSearchEngine: e.target.value as 'elasticsearch' | 'neo4j' })}
              className="border rounded px-3 py-2"
              required
            >
              <option value="elasticsearch">Elasticsearch</option>
              <option value="neo4j">Grafo de Conhecimento (Neo4j)</option>
            </select>
          </div>

          {activeTab === 'elasticsearch' ? renderElasticsearchConfig() : renderNeo4jConfig()}

          <div className="flex justify-end gap-2 mt-6 pt-6 border-t">
            <Button variant="secondary" size="sm" type="button" onClick={() => fetchConfig()}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
} 